import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { FaFilePdf } from 'react-icons/fa';
import { IoCloseOutline } from 'react-icons/io5';
import GlassButton from './ui/GlassButton';

const WhitepaperModal = ({ isOpen, onClose, whitepaper }) => {
  const modalRef = useRef(null);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle click outside to close
  const handleClickOutside = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  if (!whitepaper) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-10"
          onClick={handleClickOutside}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />

          {/* Modal Container */}
          <motion.div
            ref={modalRef}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-[3rem] bg-black/60 border border-white/10 shadow-[0_0_50px_rgba(59,130,246,0.2)] flex flex-col"
          >
            {/* Ambient Background Glows */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px] -z-10" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-[100px] -z-10" />

            {/* Header */}
            <div className="sticky top-0 z-20 flex items-center justify-between p-8 bg-black/40 backdrop-blur-xl border-b border-white/5">
              <div className="flex flex-col">
                <div className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.4em] mb-2">
                  {whitepaper.id}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white tracking-widest">
                  {whitepaper.title}
                </h2>
              </div>
              
              <button
                onClick={onClose}
                className="p-3 rounded-2xl bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:border-blue-500/50 transition-all group"
              >
                <IoCloseOutline className="text-3xl group-hover:rotate-90 transition-transform duration-500" />
              </button>
            </div>

            {/* Content Body */}
            <div className="flex-grow overflow-y-auto p-8 md:p-12 custom-scrollbar">
              <div className="max-w-3xl mx-auto">
                <div className="text-blue-200/40 text-sm font-medium mb-12 uppercase tracking-[0.3em] flex items-center gap-4">
                  <span className="w-12 h-px bg-blue-500/20" /> Abstract
                </div>
                
                <p className="text-xl text-blue-100 font-medium italic leading-relaxed mb-16 border-l-2 border-blue-500/30 pl-8">
                  {whitepaper.desc}
                </p>

                <div className="prose prose-invert prose-blue max-w-none">
                  {whitepaper.content ? (
                    <div className="text-gray-300 leading-relaxed space-y-8 text-lg">
                      {whitepaper.content.split('\n\n').map((paragraph, i) => (
                        <p key={i}>{paragraph}</p>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-20 opacity-30 italic">
                      Technical content loading...
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer / CTA */}
            <div className="p-8 bg-black/40 backdrop-blur-xl border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400">
                  <FaFilePdf />
                </div>
                <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                  Official Technical Document (v1.0)
                </div>
              </div>

              <GlassButton
                variant="blue"
                size="md"
                className="px-8 py-4"
                onClick={() => {
                  if (whitepaper.fileName) {
                    const link = document.createElement('a');
                    link.href = `/Whitepapers/${whitepaper.fileName}`;
                    link.download = whitepaper.fileName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }
                }}
              >
                Download Whitepaper
              </GlassButton>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default WhitepaperModal;
